import {
  AppData,
  CompRef,
  DocumentServicesObject,
} from '@wix/document-services-types';
import {
  PluginInstallationParams,
  WidgetPointer,
  PluginActionContext,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentRef } from '@wix/editor-platform-sdk-types';
import { PlatformContext } from '../../../types/platformApi';
import { createPluginHostApi } from './pluginHostAPI';
import { extendPluginActionContext } from './utils';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { PluginActionFullContext } from './types';
import { SlotPointer } from './slotPointer';

// TODO: make autoPopulate logic part of initPluginsService()
async function navigateAsync(
  documentServicesAPI: DocumentServicesObject,
  pageId: string,
) {
  const promisifiedNavigation = new Promise<void>((resolve) => {
    documentServicesAPI.pages.navigateTo(
      pageId,
      () => {
        resolve();
      },
      () => {
        resolve(); // If we couldn't navigate we still want to try to add the plugin
      },
    );
  });
  return promisifiedNavigation;
}

async function addWidgetPlugin(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  slotComponentRef: ComponentRef,
  widgetRef: WidgetPointer,
  actionContext: PluginActionFullContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef> {
  const hostAPI = createPluginHostApi(documentServicesAPI, experimentsAPI);
  const slotPointer = new SlotPointer({ slotRef: slotComponentRef });
  const slotInfo = hostAPI.getWidgetSlot(slotPointer, {
    includePrivate: false,
  });

  if (slotInfo?.pluginInfo !== undefined) {
    await hostAPI.uninstallWidgetPlugin(context, slotPointer, actionContext);
  }

  return hostAPI.installWidgetPlugin(
    context,
    slotPointer,
    widgetRef,
    actionContext,
  );
}

function populate(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  pluginInstallationParams: PluginInstallationParams,
  actionContext: PluginActionFullContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef> {
  return addWidgetPlugin(
    context,
    documentServicesAPI,
    pluginInstallationParams.slotComponentRef,
    pluginInstallationParams.widgetRef,
    actionContext,
    experimentsAPI,
  );
}
export async function autoInstall(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  pluginInstallationParams: PluginInstallationParams,
  pluginActionContext: PluginActionContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef | null> {
  const actionContext = extendPluginActionContext(pluginActionContext);
  const pluginRef = await populate(
    context,
    documentServicesAPI,
    pluginInstallationParams,
    actionContext,
    experimentsAPI,
  );

  return pluginRef;
}

export async function autoInstallWithNavigation(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  pluginInstallationParams: PluginInstallationParams,
  pluginActionContext: PluginActionContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef | null> {
  await navigateAsync(documentServicesAPI, pluginInstallationParams.pageId);

  await documentServicesAPI.waitForChangesAppliedAsync();
  const actionContext = extendPluginActionContext(pluginActionContext);

  const pluginRef = await populate(
    context,
    documentServicesAPI,
    pluginInstallationParams,
    actionContext,
    experimentsAPI,
  );

  await documentServicesAPI.waitForChangesAppliedAsync();

  await (documentServicesAPI.pages as any).scrollToComponent(pluginRef.id); // for some reason type is missing and its actually exists

  context.platformApiMethods.editor.selection.selectComponentByCompRef(
    {} as AppData,
    '',
    { compsToSelect: [pluginRef] },
  );

  return pluginRef;
}
